import type { IBulletChartConfig, IBulletChartRange } from './types';

import { toPercent } from '../utils';

// ----------------------------------------------------------------------------

export { toPercent };

export type TypeResolvedRange = IBulletChartRange & {
    left: number;
    width: number;
};

// ----------------------------------------------------------------------------

export function isInRange(value: number, normalMin: number, normalMax: number): boolean {
    return value >= normalMin && value <= normalMax;
}

export function getNormalRange(config: IBulletChartConfig): { normalMin: number; normalMax: number } | null {
    const { normalMin, normalMax } = config;
    if (typeof normalMin === 'number' && typeof normalMax === 'number') {
        return { normalMin, normalMax };
    }
    return null;
}

export function resolveScale(config: IBulletChartConfig): { min: number; max: number } {
    if (typeof config.min === 'number' && typeof config.max === 'number') {
        return { min: config.min, max: config.max };
    }
    const normalRange = getNormalRange(config);
    if (normalRange != null) {
        const span = normalRange.normalMax - normalRange.normalMin;
        const padding = config.center === true
            ? (typeof config.centerPadding === 'number' ? config.centerPadding : span * 0.75)
            : span * 1.5;
        return {
            min: config.center === true ? normalRange.normalMin - padding : Math.max(0, normalRange.normalMin - padding),
            max: normalRange.normalMax + padding,
        };
    }
    return { min: 0, max: 100 };
}

export function resolveRanges(config: IBulletChartConfig, min: number, max: number): TypeResolvedRange[] {
    const source: IBulletChartRange[] =
        Array.isArray(config.ranges) && config.ranges.length > 0
            ? config.ranges
            : config.normalMin != null && config.normalMax != null
              ? [
                    { value: config.normalMin },
                    { value: config.normalMax, color: 'success' },
                    { value: max },
                ]
              : [];

    let accumulated = min;

    return source.map((range) => {
        const left = toPercent(accumulated, min, max);
        const right = toPercent(range.value, min, max);
        accumulated = range.value;
        return { ...range, left, width: right - left };
    });
}

export function resolveRangeLabel(config: IBulletChartConfig): string | null {
    if (typeof config.rangeLabel === 'string') {
        return config.rangeLabel;
    }
    if (config.rangeLabel === true && config.normalMin != null && config.normalMax != null) {
        return config.normalMin + ' – ' + config.normalMax;
    }
    return null;
}
